import React, { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, Calendar, FileText } from "lucide-react";

export default function Privacy() {
  const [activeSection, setActiveSection] = useState("overview");

  const sections = [
    { id: "overview", title: "Overview" },
    { id: "information-collection", title: "Information We Collect" },
    { id: "information-usage", title: "How We Use Your Information" },
    { id: "information-sharing", title: "Information Sharing" },
    { id: "data-security", title: "Data Security" },
    { id: "data-retention", title: "Data Retention" },
    { id: "user-rights", title: "Your Rights" },
    { id: "cookies", title: "Cookies and Tracking" },
    { id: "third-party", title: "Third-Party Services" },
    { id: "international-transfers", title: "International Data Transfers" },
    { id: "policy-changes", title: "Changes to This Policy" },
    { id: "contact", title: "Contact Us" }
  ];

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header currentPage="legal" variant="gallery" />
      
      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="w-80 bg-slate-50 dark:bg-slate-800 min-h-screen pt-8 pb-16 sticky top-0 border-r border-slate-200 dark:border-slate-700">
          <div className="px-6">
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Privacy Policy</h2>
              <div className="flex items-center text-sm text-slate-600 dark:text-slate-400 mb-2">
                <Calendar className="h-4 w-4 mr-2" />
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <div className="flex items-center text-sm text-slate-600 dark:text-slate-400">
                <FileText className="h-4 w-4 mr-2" />
                Version 1.0
              </div>
            </div>
            
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                    activeSection === section.id
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 font-medium'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </nav>
            
            <div className="mt-8 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-300 mb-2">Need Help?</h3>
              <p className="text-xs text-purple-700 dark:text-purple-400 mb-3">
                Questions about our privacy practices?
              </p>
              <a
                href="mailto:privacy@lavenderflow.dev"
                className="inline-flex items-center text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
              >
                <Mail className="h-3 w-3 mr-1" />
                privacy@lavenderflow.dev
              </a>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 py-12 px-8 max-w-4xl">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <section id="overview">
              <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-6">Privacy Policy</h1>
              <p className="text-xl text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
                At Lavender Flow, we respect your privacy and are committed to protecting your personal data. 
                This privacy policy explains how we collect, use, and safeguard your information when you use our platform.
              </p>
            </section>

            <section id="information-collection" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">1. Information We Collect</h2>
              <p className="mb-4">
                We collect information you provide directly to us, such as when you create an account, 
                use our services, or contact us for support. This may include:
              </p>
              <ul className="mb-6">
                <li>Name and email address</li>
                <li>Account credentials</li>
                <li>Workflow data and configurations</li>
                <li>Usage analytics and performance data</li>
                <li>Support communications</li>
              </ul>
            </section>

            <section id="information-usage" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">2. How We Use Your Information</h2>
              <p className="mb-4">We use the information we collect to:</p>
              <ul className="mb-6">
                <li>Provide and maintain our services</li>
                <li>Process your workflows and store your data</li>
                <li>Send you technical notices and support messages</li>
                <li>Improve our platform and develop new features</li>
                <li>Monitor usage and prevent abuse</li>
              </ul>
            </section>

            <section id="information-sharing" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">3. Information Sharing</h2>
              <p className="mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties 
                except in the following circumstances:
              </p>
              <ul className="mb-6">
                <li>With your explicit consent</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights and prevent fraud</li>
                <li>With service providers who assist in our operations (under strict confidentiality agreements)</li>
              </ul>
            </section>

            <section id="data-security" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">4. Data Security</h2>
              <p className="mb-4">
                We implement appropriate security measures to protect your personal information against 
                unauthorized access, alteration, disclosure, or destruction. This includes:
              </p>
              <ul className="mb-6">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and assessments</li>
                <li>Access controls and authentication measures</li>
                <li>Secure hosting infrastructure</li>
              </ul>
            </section>

            <section id="data-retention" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">5. Data Retention</h2>
              <p className="mb-6">
                We retain your personal information for as long as necessary to provide our services 
                and fulfill the purposes outlined in this policy. You may request deletion of your 
                account and associated data at any time.
              </p>
            </section>

            <section id="user-rights" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">6. Your Rights</h2>
              <p className="mb-4">You have the right to:</p>
              <ul className="mb-6">
                <li>Access and update your personal information</li>
                <li>Request deletion of your data</li>
                <li>Withdraw consent for data processing</li>
                <li>Export your workflow data</li>
                <li>Object to certain types of data processing</li>
              </ul>
            </section>

            <section id="cookies" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">7. Cookies and Tracking</h2>
              <p className="mb-6">
                We use cookies and similar technologies to enhance your experience, analyze usage, 
                and provide personalized content. You can control cookie settings through your browser.
              </p>
            </section>

            <section id="third-party" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">8. Third-Party Services</h2>
              <p className="mb-6">
                Our platform integrates with various third-party AI services (OpenAI, Google, etc.). 
                When you use these integrations, your data may be processed by these services according 
                to their own privacy policies.
              </p>
            </section>

            <section id="international-transfers" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">9. International Data Transfers</h2>
              <p className="mb-6">
                Your information may be transferred to and processed in countries other than your own. 
                We ensure appropriate safeguards are in place to protect your data in such transfers.
              </p>
            </section>

            <section id="policy-changes" className="mt-12">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">10. Changes to This Policy</h2>
              <p className="mb-6">
                We may update this privacy policy from time to time. We will notify you of any material 
                changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section id="contact" className="mt-12 mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">11. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about this privacy policy or our data practices, please contact us:
              </p>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Privacy Officer</h4>
                    <a href="mailto:privacy@lavenderflow.dev" className="inline-flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300">
                      <Mail className="h-4 w-4 mr-2" />
                      privacy@lavenderflow.dev
                    </a>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Support Center</h4>
                    <a href="https://lavenderflow.dev/help" className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300">
                      https://lavenderflow.dev/help
                    </a>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
      
      <Footer />
    </div>
  );
}